package cz.lhoracek.mapl.ui.screens.newsession

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import cz.lhoracek.mapl.data.model.Player
import cz.lhoracek.mapl.data.repository.SessionRepository
import cz.lhoracek.mapl.domain.normalizeBetToPowerOfTwo
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

val PLAYER_COLORS = listOf("#E53935", "#1E88E5", "#43A047", "#FB8C00", "#E91E8C", "#00BCD4")

data class PlayerDraft(val id: String, val name: String, val color: String)

data class NewSessionUiState(
    val sessionName: String = "",
    val players: List<PlayerDraft> = listOf(
        PlayerDraft("p1", "", PLAYER_COLORS[0]),
        PlayerDraft("p2", "", PLAYER_COLORS[1]),
        PlayerDraft("p3", "", PLAYER_COLORS[2]),
    ),
    val baseBet: Int = 1,
    val potEnabled: Boolean = false,
    val isCreating: Boolean = false,
    val createdSessionId: String? = null,
    val error: String? = null,
)

class NewSessionViewModel(private val repository: SessionRepository) : ViewModel() {

    private val _uiState = MutableStateFlow(NewSessionUiState())
    val uiState: StateFlow<NewSessionUiState> = _uiState.asStateFlow()

    fun setSessionName(name: String) = _uiState.update { it.copy(sessionName = name) }

    fun updatePlayer(
        index: Int,
        name: String = _uiState.value.players[index].name,
        color: String = _uiState.value.players[index].color,
    ) {
        _uiState.update { state ->
            val updated = state.players.toMutableList()
            updated[index] = updated[index].copy(name = name, color = color)
            state.copy(players = updated)
        }
    }

    fun addPlayer() {
        if (_uiState.value.players.size >= 4) return
        _uiState.update { state ->
            val newId = "p${state.players.size + 1}"
            val color = PLAYER_COLORS.getOrElse(state.players.size) { PLAYER_COLORS[0] }
            state.copy(players = state.players + PlayerDraft(newId, "", color))
        }
    }

    fun removePlayer(index: Int) {
        if (_uiState.value.players.size <= 3) return
        _uiState.update { state ->
            state.copy(players = state.players.toMutableList().also { it.removeAt(index) })
        }
    }

    fun setBaseBet(bet: Int) = _uiState.update { it.copy(baseBet = normalizeBetToPowerOfTwo(bet)) }
    fun setPotEnabled(enabled: Boolean) = _uiState.update { it.copy(potEnabled = enabled) }

    fun createSession() {
        val state = _uiState.value
        if (state.isCreating) return
        if (state.players.any { it.name.isBlank() }) return
        _uiState.update { it.copy(isCreating = true, error = null) }
        viewModelScope.launch {
            try {
                val players = state.players.map { draft ->
                    Player(id = draft.id, name = draft.name.trim(), color = draft.color)
                }
                val sessionName = state.sessionName.ifBlank {
                    players.joinToString(", ") { it.name }
                }
                val id = repository.createSession(
                    name = sessionName,
                    players = players,
                    baseBet = state.baseBet,
                    potEnabled = state.potEnabled,
                )
                _uiState.update { it.copy(isCreating = false, createdSessionId = id) }
            } catch (e: Exception) {
                _uiState.update { it.copy(isCreating = false, error = "Nepodařilo se vytvořit partii. Zkontroluj připojení k internetu.") }
            }
        }
    }

    fun dismissError() = _uiState.update { it.copy(error = null) }

    fun consumeNavigation() = _uiState.update { it.copy(createdSessionId = null) }

    fun resetForm() {
        _uiState.value = NewSessionUiState()
    }
}
